import React, { useEffect, useState, useCallback } from "react";
import { Scale, Shield, Drama, Download, FileText, FileSpreadsheet } from "lucide-react";
import { FilterBar, buildQuery } from "@/components/FilterBar";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

function SectionCard({ testId, title, icon: Icon, accent, stats }) {
  return (
    <Card className="border-slate-200 p-5 shadow-sm animate-fade-in-up" data-testid={testId}>
      <div className="mb-4 flex items-center gap-2.5">
        <span className={`flex h-10 w-10 items-center justify-center rounded-lg ring-1 ${accent}`}><Icon className="h-5 w-5" /></span>
        <h3 className="font-heading text-lg font-semibold text-slate-800">{title}</h3>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-4">
        {stats.map((s) => (
          <div key={s.testId} data-testid={s.testId}>
            <p className={`font-heading text-2xl font-extrabold tabular-nums ${s.tone || "text-slate-900"}`}>{s.value ?? "—"}</p>
            <p className="mt-0.5 text-xs font-semibold uppercase tracking-wide text-slate-400">{s.label}</p>
          </div>
        ))}
      </div>
    </Card>
  );
}

export default function Reports() {
  const [filter, setFilter] = useState({ district_id: "", campus_id: "", team_id: "", year: "" });
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/reports/summary?${buildQuery(filter)}`);
    setD(data);
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const download = async (entity, format, detail) => {
    setBusy(`${entity}-${format}-${detail}`);
    try {
      const q = buildQuery(filter);
      const url = `${BASE}/admin/exports/${entity}?format=${format}&detail=${detail}${q ? `&${q}` : ""}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem("rallypointgo_token")}` } });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${entity}_${detail ? "detail" : "summary"}.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Export downloaded");
    } catch (e) { toast.error(e.message); }
    finally { setBusy(""); }
  };

  const ExportBlock = ({ entity, title, accent }) => (
    <Card className="p-6 border-slate-200 shadow-sm" data-testid={`export-block-${entity}`}>
      <div className="mb-4 flex items-center gap-2">
        <span className={`flex h-10 w-10 items-center justify-center rounded-lg ${accent}`}><Download className="h-5 w-5" /></span>
        <h3 className="text-lg font-semibold text-slate-800 font-heading">{title}</h3>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {[["Detail", true], ["Summary", false]].map(([label, detail]) => (
          <div key={label} className="rounded-lg border border-slate-200 p-3">
            <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">{label}</p>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" className="rounded-full" disabled={!!busy} onClick={() => download(entity, "csv", detail)} data-testid={`export-${entity}-csv-${detail ? "detail" : "summary"}`}>
                <FileSpreadsheet className="mr-1.5 h-4 w-4" /> CSV
              </Button>
              <Button size="sm" variant="outline" className="rounded-full" disabled={!!busy} onClick={() => download(entity, "pdf", detail)} data-testid={`export-${entity}-pdf-${detail ? "detail" : "summary"}`}>
                <FileText className="mr-1.5 h-4 w-4" /> PDF
              </Button>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );

  return (
    <div>
      <PageHeader title="Reports" subtitle="Summary across district, campus, team and year" />
      <FilterBar value={filter} onChange={setFilter} />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        <SectionCard testId="report-merit-card" title="Merit System" icon={Scale} accent="bg-sky-50 text-sky-600 ring-sky-100" stats={[
          { testId: "report-merit-cases", label: "Merit Cases", value: d?.merit_cases },
          { testId: "report-total-merits", label: "Total Merits", value: d?.total_merits, tone: "text-emerald-600" },
          { testId: "report-total-demerits", label: "Total Demerits", value: d?.total_demerits, tone: "text-red-600" },
          { testId: "report-net-score", label: "Net Score", value: d?.net_score, tone: "text-sky-700" },
        ]} />
        <SectionCard testId="report-lettering-card" title="Letter Awards" icon={Shield} accent="bg-violet-100 text-violet-700 ring-violet-200" stats={[
          { testId: "report-lettering-subs", label: "Submissions", value: d?.lettering_submissions },
          { testId: "report-lettering-approved", label: "Approved", value: d?.lettering_approved, tone: "text-emerald-600" },
          { testId: "report-lettering-pending", label: "Pending", value: d?.lettering_pending, tone: "text-amber-600" },
          { testId: "report-approved-points", label: "Approved Points", value: d?.approved_lettering_points, tone: "text-violet-700" },
        ]} />
        <SectionCard testId="report-thespian-card" title="Thespian Points" icon={Drama} accent="bg-amber-100 text-amber-700 ring-amber-200" stats={[
          { testId: "report-thespian-claims", label: "Claims", value: d?.thespian_claims },
          { testId: "report-thespian-approved", label: "Approved", value: d?.thespian_approved, tone: "text-emerald-600" },
          { testId: "report-thespian-pending", label: "Pending", value: d?.thespian_pending, tone: "text-amber-600" },
          { testId: "report-thespian-points", label: "Approved Points", value: d?.thespian_approved_points, tone: "text-amber-700" },
        ]} />
      </div>
      <h2 className="mt-8 mb-3 text-lg font-semibold text-slate-800 font-heading">Exports</h2>
      <p className="mb-4 text-sm text-slate-500">Download detailed or summary reports (CSV / PDF) for the filters selected above.</p>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 2xl:grid-cols-3">
        <ExportBlock entity="merits" title="Merits Export" accent="bg-emerald-50 text-emerald-600" />
        <ExportBlock entity="lettering" title="Lettering Export" accent="bg-violet-50 text-violet-600" />
        <ExportBlock entity="thespian" title="Thespian Points Export" accent="bg-amber-50 text-amber-700" />
      </div>
    </div>
  );
}
